import { uniqBy } from 'lodash-es';

import type { AssetsGetShareSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/assets';
import type { SearchSearchCriteriaSchema } from '@iconik-io/iconik-api/src/services/search';
import {
  OBJECT_TYPE_COLLECTIONS,
  type ObjectType,
  type SearchObjectType,
} from '@iconik-io/iconik-api/src/types/object_types';
import { COLOR_CODING } from '@iconik-io/shared-lib/src/utils/constants/player';
import type { UserRoles } from '@iconik-io/shared-lib/src/utils/roles';

import {
  COMMENTS_KEY,
  FACE_RECOGNITION,
  FILES_KEY,
  HISTORY_KEY,
  POSTERS_KEY,
  RELATIONS_KEY,
  REVIEWS_KEY,
  SEARCH_COLLECTIONS_PANEL,
  SUBCLIPS_KEY,
  TIME_BASED_METADATA_KEY,
  TRANSCRIPTIONS_KEY,
  UPLOADS_PANEL,
} from '../../../constants/data';
import {
  ACLS_HELP,
  COMMENTS,
  FILES,
  HISTORY,
  METADATA,
  POSTERS,
  RELATIONS,
  REVIEW_AND_APPROVE,
  SHARES,
  SUBCLIPS,
  TIME_BASED_METADATA,
  TRANSCRIPTIONS,
} from '../../../constants/help_urls';
import type { CommonAclsPrefix } from '../../../ducks/common_acls/types';
import { AssetCommentsPanel } from '../../../panels/asset_comments/asset_comments';
import { UadCommentsPanel } from '../../../panels/asset_comments/uad_comments_panel';
import AssetFaceRecognitionPanel from '../../../panels/asset_face_recognition/asset_face_recognition';
import FilesPanel from '../../../panels/asset_files/asset_files';
import AssetMetadataPanel from '../../../panels/asset_metadata/asset_metadata';
import PublishPanel from '../../../panels/asset_publish/asset_publish';
import AssetRelationsPanel from '../../../panels/asset_relations/asset_relations';
import AssetReviewPanel from '../../../panels/asset_review/asset_review';
import { AssetSubclipsPanel } from '../../../panels/asset_subclips/asset_subclips';
import { AssetTimeBasedMetadataPanel } from '../../../panels/asset_time_based_metadata/asset_time_based_metadata';
import AssetTranscriptionsPanel from '../../../panels/asset_transcriptions/asset_transcriptions';
import CollectionsPanel from '../../../panels/collections/collections';
import EntityAclsPanel from '../../../panels/entity_acls/entity_acls';
import { EntityHistoryPanel } from '../../../panels/entity_history/entity_history';
import EntityPostersPanel from '../../../panels/entity_posters/entity_posters';
import SharesPanel from '../../../panels/shares/shares';
import { UploadsWidgetPanel } from '../../../panels/uploads/uploads';
import type { PermissionValues } from '../../../types/common';
import { extractFiltersFromSearchDocument } from '../../../utils/search_utils/search_utils';
import type { BadgeType } from '../../sidebar_menu/badge';
import type { PanelItem } from '../types';
import { panelPropsGuard } from '../utils';

import sharePageStyles from './share_collection/share_collection.module.css';

export const calculateSearchFiltersNumber = (
  searchFilters?: SearchSearchCriteriaSchema,
): number => {
  let filtersCount = 0;
  if (searchFilters?.facets_filters?.length) {
    filtersCount += searchFilters.facets_filters.length;
  }

  const filters = extractFiltersFromSearchDocument(searchFilters);

  const filtersTermsAND = filters
    .filter(({ operator }) => operator === 'AND')
    .map(({ terms }) => terms);
  const filtersTermsNOT = filters
    .filter(({ operator }) => operator === 'NOT')
    .map(({ terms }) => terms);

  const filtersTerms = uniqBy(
    [...(filtersTermsAND[0] ?? []), ...(filtersTermsNOT[0] ?? [])],
    'name',
  );
  if (filtersTerms.length) {
    const nonStatusTerms = filtersTerms.filter(
      (term) => term?.name !== 'status',
    );
    if (nonStatusTerms.length) {
      filtersCount += nonStatusTerms.length;
    }
  }

  return filtersCount;
};

export const canUpload = (roles: UserRoles): boolean =>
  roles.canWriteAssets &&
  roles.canCreateAssets &&
  roles.canCreateFormats &&
  roles.canWriteFormats &&
  roles.canWriteFiles &&
  roles.canReadStorages &&
  roles.webCanUpload;

export const getShareCollectionsPanelItem = (
  id: string,
  active = false,
): PanelItem =>
  panelPropsGuard({
    title: 'Collections',
    key: SEARCH_COLLECTIONS_PANEL,
    iconName: 'collection',
    component: CollectionsPanel,
    active,
    className: sharePageStyles.collections_panel,
    props: {
      aclsType: OBJECT_TYPE_COLLECTIONS,
      aclsObjectId: id,
    },
  });

export const getUploaderPanelItem = (
  _roles: UserRoles,
  badge: BadgeType,
): PanelItem =>
  panelPropsGuard({
    title: 'Upload',
    key: UPLOADS_PANEL,
    iconName: 'load_up',
    component: UploadsWidgetPanel,
    props: {},
    color: COLOR_CODING.uploads,
    badge,
    badgeColor: 'var(--color-validation-success-default)',
    bottom: true,
    widget: true,
  });

export const getAclsPanelItem = (
  key: string,
  id: string | undefined,
  aclsType: CommonAclsPrefix,
  disabled = false,
  active = false,
): PanelItem =>
  panelPropsGuard({
    title: 'Access control',
    key,
    iconName: 'access_control',
    color: COLOR_CODING.acls,
    component: EntityAclsPanel,
    props: {
      objectId: id,
      aclsType,
      stickyForm: true,
      useEmptyState: true,
    },
    side: 'right',
    defaultSize: 480,
    disabled,
    active,
    helpButton: { path: ACLS_HELP, title: 'Access Control' },
  });

export const getMetadataPanelItem = (
  entity: SearchObjectType | undefined,
  key: string,
  categoryName: string | undefined,
  disabled = false,
  active = false,
  entityPermissions?: PermissionValues,
  share?: AssetsGetShareSchemaWithIdNullable,
): PanelItem =>
  panelPropsGuard({
    title: 'Metadata',
    key,
    iconName: 'Svg2022_uad_metadata',
    component: AssetMetadataPanel,
    props: {
      entity,
      entityPermissions,
      share,
      categoryName,
    },
    side: 'right',
    color: COLOR_CODING.metadata,
    disabled,
    active,
    helpButton: { path: METADATA, title: 'Metadata' },
  });

export const getPostersPanelItem = (
  title: string,
  entity: ObjectType,
  disabled = false,
  active = false,
): PanelItem =>
  panelPropsGuard({
    title,
    key: POSTERS_KEY,
    iconName: 'snapshot',
    component: EntityPostersPanel,
    side: 'right',
    color: COLOR_CODING.posters,
    props: {
      entity,
    },
    disabled,
    active,
    helpButton: { path: POSTERS, title: 'Posters' },
  });

export const getFaceRecognitionPanelItem = (
  title: string,
  _entity: ObjectType,
  disabled = false,
  active = false,
  hasDetectedUnconfirmedPersons = false,
  hasDetectedNewPersons = false,
): PanelItem => {
  const showBadge = hasDetectedUnconfirmedPersons || hasDetectedNewPersons;
  return panelPropsGuard({
    title,
    key: FACE_RECOGNITION,
    iconName: 'face_recognition',
    component: AssetFaceRecognitionPanel,
    side: 'right',
    color: COLOR_CODING.face_recognition,
    props: {},
    disabled,
    active,
    defaultSize: 420,
    minSize: 380,
    badge: showBadge ? 'empty' : undefined,
    badgeColor: showBadge ? 'var(--color-validation-error-default)' : undefined,
  });
};

export const getSharesPanelItem = ({
  key,
  disabled = false,
  active = false,
}: {
  key: string;
  disabled?: boolean;
  active?: boolean;
}): PanelItem =>
  panelPropsGuard({
    title: 'Shares',
    key,
    iconName: 'share',
    component: SharesPanel,
    props: {},
    side: 'right',
    minSize: 388,
    color: COLOR_CODING.shares,
    disabled,
    active,
    helpButton: { path: SHARES, title: 'Shares' },
  });

export const getPublishPanelItem = ({
  key,
  disabled,
  disabledTooltipText,
}: {
  key: string;
  disabled: boolean;
  disabledTooltipText?: string;
}): PanelItem =>
  panelPropsGuard({
    title: 'Publish',
    key,
    iconName: 'send',
    component: PublishPanel,
    props: {},
    side: 'right',
    defaultSize: 450,
    color: COLOR_CODING.publish,
    disabled,
    disabledTooltipText,
  });

export const getCommentsPanelItem = (
  active = true,
  isLegacyShare?: boolean,
): PanelItem =>
  panelPropsGuard({
    title: 'Comments',
    key: COMMENTS_KEY,
    iconName: 'comment',
    active,
    component: isLegacyShare ? AssetCommentsPanel : UadCommentsPanel,
    props: {},
    side: 'right',
    color: COLOR_CODING.comments,
    defaultSize: 400,
    helpButton: { path: COMMENTS, title: 'Comments' },
  });

export const getTranscriptionsPanelItem = (
  id: string | undefined,
  active = true,
): PanelItem =>
  panelPropsGuard({
    title: 'Transcriptions',
    key: TRANSCRIPTIONS_KEY,
    reactKey: `transcriptions-${id}`,
    iconName: 'transcription',
    active,
    component: AssetTranscriptionsPanel,
    props: {},
    side: 'right',
    color: COLOR_CODING.transcriptions,
    defaultSize: 560,
    helpButton: { path: TRANSCRIPTIONS, title: 'Transcriptions' },
  });

export const reviewAndApprovePanelItem = (active = true): PanelItem =>
  panelPropsGuard({
    title: 'Review & Approve',
    key: REVIEWS_KEY,
    iconName: 'ra_approved',
    active,
    component: AssetReviewPanel,
    props: {},
    side: 'right',
    color: COLOR_CODING.reviews,
    helpButton: { path: REVIEW_AND_APPROVE, title: 'Review & Approve' },
  });

export const getTimeBasedMetadataPanelItem = (active = true): PanelItem =>
  panelPropsGuard({
    title: 'Time-Based Metadata',
    key: TIME_BASED_METADATA_KEY,
    iconName: 'Svg2022_uad_time_based_metadata',
    active,
    component: AssetTimeBasedMetadataPanel,
    side: 'right',
    color: COLOR_CODING.metadata,
    defaultSize: 400,
    props: {},
    helpButton: { path: TIME_BASED_METADATA, title: 'Time-Based Metadata' },
  });

export const getSubclipsPanelItem = (active = true): PanelItem =>
  panelPropsGuard({
    title: 'Subclips',
    key: SUBCLIPS_KEY,
    iconName: 'asset_type_subclip',
    active,
    component: AssetSubclipsPanel,
    side: 'right',
    color: COLOR_CODING.subclips,
    defaultSize: 400,
    props: {},
    helpButton: { path: SUBCLIPS, title: 'Subclips' },
  });

export const getFilesPanelItem = (active = true): PanelItem =>
  panelPropsGuard({
    title: 'Files',
    key: FILES_KEY,
    iconName: 'file',
    active,
    component: FilesPanel,
    props: {},
    side: 'right',
    defaultSize: 650,
    color: COLOR_CODING.files,
    helpButton: { path: FILES, title: 'Files' },
  });

export const getAssetHistoryPanelItem = (active = true): PanelItem =>
  panelPropsGuard({
    title: 'History',
    key: HISTORY_KEY,
    iconName: 'history',
    active,
    component: EntityHistoryPanel,
    side: 'right',
    defaultSize: 450,
    color: COLOR_CODING.history,
    props: {},
    helpButton: { path: HISTORY, title: 'History' },
  });

export const getRelationsPanelItem = (active = false): PanelItem =>
  panelPropsGuard({
    title: 'Relations',
    key: RELATIONS_KEY,
    iconName: 'relations',
    active,
    props: {},
    component: AssetRelationsPanel,
    side: 'right',
    color: COLOR_CODING.relations,
    defaultSize: 350,
    helpButton: { path: RELATIONS, title: 'Relations' },
  });
